import { useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
	CheckinOrderFragment,
	CheckoutOrderFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderStatus
} from '@websky/graphql';
import { CheckoutSteps } from './types';
import { CartService } from '../Cart/types';
import { Steps } from '../BaseComponents/Stepbar/types';
import { useToggleable } from '../hooks';
import { convertCartServiceProps, isCheckoutOrder as getIsCheckoutOrder } from './utils';
import { CheckinStep } from '../Modules/Checkin/types';
import { ModeContext } from '../context';
import { Mode } from '../types';

const CART_SERVICE_MAP: {
	[key in CheckoutSteps | CheckinStep]?: CartService;
} = {
	[CheckoutSteps.Flights]: CartService.Flights,
	[CheckoutSteps.Seats]: CartService.Seats,
	[CheckoutSteps.Baggage]: CartService.Baggage,
	[CheckoutSteps.Meal]: CartService.Meal,
	[CheckoutSteps.Extras]: CartService.Extra,
	[CheckoutSteps.Passengers]: CartService.Passengers,
	[CheckoutSteps.Insurance]: CartService.Insurances
};

const REQUIRED_CHECKOUT_MOBILE_STEPS = [CartService.Flights, CartService.Seats, CartService.Baggage, CartService.Meal];

export const useMobileStepbar = (
	order: CheckoutOrderFragment | CheckinOrderFragment,
	stepbarConfig: Steps,
	alwaysOpen = false
) => {
	const mode = useContext(ModeContext);
	const location = useLocation();

	const { isOpen, toggle, close } = useToggleable(alwaysOpen);

	useEffect(() => {
		close();
	}, [location.pathname]);

	const requiredSteps = useMemo(() => {
		if (mode === Mode.Checkin) {
			return [];
		}

		return REQUIRED_CHECKOUT_MOBILE_STEPS;
	}, [mode]);

	const { cartServiceProps, isCheckoutOrder } = useMemo(() => {
		return {
			cartServiceProps: convertCartServiceProps(order),
			isCheckoutOrder: getIsCheckoutOrder(order)
		};
	}, [order]);

	const [isBookedOrConfirmed, isCancelled] = useMemo(() => {
		return [
			order.status === OrderStatus.Booked || order.status === OrderStatus.Confirmed,
			order.status === OrderStatus.Cancelled
		];
	}, [order.status]);

	const availableSteps = useMemo(() => {
		return Object.values(stepbarConfig ?? {}).filter(step => !step.isHidden);
	}, [stepbarConfig]);

	const hasIncludedBaggage = useMemo(() => {
		return order.travellers.some(traveller =>
			traveller.services.brandIncludedServices.services.find(
				({ service }) => service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage
			)
		);
	}, [order.travellers]);

	const [cartServices, additionalSteps] = useMemo(() => {
		const cartServicesSet = new Set<CartService>(requiredSteps);
		const additionalStepsSet = new Set<CheckoutSteps>();

		availableSteps.forEach(step => {
			const cartService = CART_SERVICE_MAP[step.type];

			if (cartService !== undefined) {
				cartServicesSet.add(cartService);
			} else {
				additionalStepsSet.add(step.type as CheckoutSteps);
			}
		});

		if (isBookedOrConfirmed || isCancelled) {
			if (!cartServicesSet.has(CartService.Passengers) && additionalStepsSet.has(CheckoutSteps.Order)) {
				cartServicesSet.add(CartService.Passengers);
				additionalStepsSet.delete(CheckoutSteps.Order);
			}

			if (isCheckoutOrder && !isCancelled) {
				cartServicesSet.add(CartService.Extra);
				cartServicesSet.add(CartService.Misc);
			}
		}

		if (hasIncludedBaggage) {
			cartServicesSet.add(CartService.Baggage);
		}

		return [[...cartServicesSet], [...additionalStepsSet]];
	}, [order, availableSteps, requiredSteps, isBookedOrConfirmed, isCheckoutOrder, isCancelled]);

	const isPayment = useMemo(() => {
		return order.isBlockedForPayment || location.pathname.includes('payment');
	}, [order.isBlockedForPayment, location.pathname]);

	return {
		isOpen,
		toggle,
		close,

		availableSteps,
		cartServices,
		additionalSteps,
		cartServiceProps,
		isBookedOrConfirmed,
		isCheckoutOrder,
		isCancelled,
		isPayment
	};
};
